import { ISSUE_TEXT_MAX_LENGTH, ISSUE_TITLE_MAX_LENGTH } from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIssueMgmtWrite } from '../../hooks/useIssueMgmt.js';
import { ISSUE_VOICE_SOURCE_LABEL_KEYS, jstToday } from '../../lib/issue-mgmt-format.js';
import { ChoiceChips } from './ChoiceChips.js';
import { CountedTextField, withinLimit } from './CountedTextField.js';
import { useIssueToast } from './useIssueToast.js';

/**
 * 小窓で選ぶもの（声を入れる／課題を作る）。
 * @graph-connects none
 */
type EntryMode = 'voice' | 'issue';

/**
 * 手で入れた声の発生元。
 * @graph-connects none
 */
type VoiceSource = keyof typeof ISSUE_VOICE_SOURCE_LABEL_KEYS;

/**
 * 発生元の並び。
 * @graph-connects none
 */
const VOICE_SOURCES: readonly VoiceSource[] = ['phone', 'meeting', 'chat', 'other'];

/**
 * 空白だけの文を null にする。
 * @graph-connects none
 */
function optionalText(value: string): string | null {
  return value.trim() === '' ? null : value;
}

/**
 * 「声を入れる／課題を作る」の小窓（SC-07）。
 * 声は本文・発生元・日付を入れて保存する（振り分けは「やること」で行う）。課題はタイトル（と任意で中身3項目）を入れて
 * 判定待ちで作り、作った課題の詳細を開く。どちらも保存時に伏せ字を当て、当てたらそう知らせる。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Slack 以外で聞いた声（電話・会議・チャットなど）を入れ、声の無い課題を判定待ちで直接作る小窓
 * @graph-connects useIssueMgmtWrite [calls] createVoice・createIssue
 * @graph-connects useIssueToast [calls] 保存の知らせ
 */
export function NewEntryDialog({
  open,
  onClose,
  onIssueCreated,
  now = new Date(),
}: {
  open: boolean;
  onClose: () => void;
  onIssueCreated: (issueId: string) => void;
  now?: Date;
}) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const createVoice = useIssueMgmtWrite('createVoice');
  const createIssue = useIssueMgmtWrite('createIssue');
  const today = jstToday(now);
  const [mode, setMode] = useState<EntryMode>('voice');
  const [body, setBody] = useState('');
  const [source, setSource] = useState<VoiceSource | null>(null);
  const [postedDate, setPostedDate] = useState(today);
  const [title, setTitle] = useState('');
  const [what, setWhat] = useState('');
  const [guidance, setGuidance] = useState('');
  const [fixedText, setFixedText] = useState('');

  const reset = () => {
    setBody('');
    setSource(null);
    setPostedDate(today);
    setTitle('');
    setWhat('');
    setGuidance('');
    setFixedText('');
  };
  const close = () => {
    reset();
    onClose();
  };

  const textsFit = [what, guidance, fixedText].every((v) => withinLimit(v, ISSUE_TEXT_MAX_LENGTH));
  const voiceReady =
    body.trim() !== '' &&
    withinLimit(body, ISSUE_TEXT_MAX_LENGTH) &&
    source !== null &&
    postedDate !== '' &&
    postedDate <= today;
  const issueReady = title.trim() !== '' && withinLimit(title, ISSUE_TITLE_MAX_LENGTH) && textsFit;
  const ready = mode === 'voice' ? voiceReady : issueReady;
  const saving = createVoice.isPending || createIssue.isPending;

  const submitVoice = (voiceSource: VoiceSource) => {
    createVoice.mutate(
      { body, source: voiceSource, postedDate },
      {
        onSuccess: (result) => {
          toast.notifySaved(t('issueMgmt.newEntry.voiceSaved', { id: result.id }), {
            ...(result.maskedFields.length > 0 ? { detail: t('issueMgmt.form.masked') } : {}),
          });
          close();
        },
        onError: toast.notifyError,
      },
    );
  };

  const submitIssue = () => {
    createIssue.mutate(
      {
        title,
        what: optionalText(what),
        guidance: optionalText(guidance),
        fixed: optionalText(fixedText),
      },
      {
        onSuccess: (result) => {
          toast.notifySaved(t('issueMgmt.newEntry.issueSaved', { title: title.trim() }), {
            operationId: result.operationId,
            ...(result.maskedFields.length > 0 ? { detail: t('issueMgmt.form.masked') } : {}),
          });
          close();
          onIssueCreated(result.id);
        },
        onError: toast.notifyError,
      },
    );
  };

  const submit = () => {
    if (mode === 'issue') {
      submitIssue();
    } else if (source !== null) {
      submitVoice(source);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="lg"
      title={
        mode === 'voice' ? t('issueMgmt.newEntry.voiceTitle') : t('issueMgmt.newEntry.issueTitle')
      }
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-xs text-[var(--color-text-muted)]">
            {ready
              ? ''
              : mode === 'voice'
                ? t('issueMgmt.newEntry.voiceHint')
                : t('issueMgmt.newEntry.issueHint')}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={close}>
              {t('issueMgmt.common.cancel')}
            </Button>
            <Button disabled={!ready} loading={saving} onClick={submit}>
              {t('issueMgmt.common.save')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <ChoiceChips<EntryMode>
          label={t('issueMgmt.newEntry.mode')}
          value={mode}
          onChange={setMode}
          choices={[
            { value: 'voice', label: t('issueMgmt.newEntry.modeVoice') },
            { value: 'issue', label: t('issueMgmt.newEntry.modeIssue') },
          ]}
        />
        {mode === 'voice' ? (
          <>
            <CountedTextField
              label={t('issueMgmt.newEntry.body')}
              value={body}
              onChange={setBody}
              max={ISSUE_TEXT_MAX_LENGTH}
              multiline
              rows={5}
            />
            <div>
              <p className="mb-1 text-sm font-medium text-[var(--color-text-secondary)]">
                {t('issueMgmt.newEntry.source')}
              </p>
              <ChoiceChips<VoiceSource>
                label={t('issueMgmt.newEntry.source')}
                value={source}
                onChange={setSource}
                choices={VOICE_SOURCES.map((value) => ({
                  value,
                  label: t(ISSUE_VOICE_SOURCE_LABEL_KEYS[value]),
                }))}
              />
            </div>
            <div>
              <label
                htmlFor="issue-voice-date"
                className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]"
              >
                {t('issueMgmt.newEntry.date')}
              </label>
              <input
                id="issue-voice-date"
                type="date"
                value={postedDate}
                max={today}
                onChange={(e) => {
                  setPostedDate(e.target.value);
                }}
                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-1.5 text-sm text-[var(--color-text-primary)]"
              />
            </div>
          </>
        ) : (
          <>
            <CountedTextField
              label={t('issueMgmt.newEntry.issueName')}
              value={title}
              onChange={setTitle}
              max={ISSUE_TITLE_MAX_LENGTH}
            />
            <CountedTextField
              label={t('issueMgmt.content.what')}
              value={what}
              onChange={setWhat}
              max={ISSUE_TEXT_MAX_LENGTH}
              multiline
              rows={3}
            />
            <CountedTextField
              label={t('issueMgmt.content.guidance')}
              value={guidance}
              onChange={setGuidance}
              max={ISSUE_TEXT_MAX_LENGTH}
              multiline
              rows={3}
            />
            <CountedTextField
              label={t('issueMgmt.content.fixed')}
              value={fixedText}
              onChange={setFixedText}
              max={ISSUE_TEXT_MAX_LENGTH}
              multiline
              rows={3}
            />
          </>
        )}
      </div>
    </Modal>
  );
}
